import { useState, useCallback, useEffect, useRef } from "react";
import { useRenameDocument } from "@/hooks/use-documents";
import { ConfirmDialog } from "@/components/ui";
import { useTranslation } from "@audit-app/utils";
import type { Id, DocumentId } from "@/packages/types";

interface RenameDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  documentId: DocumentId;
  currentName: string;
}

export function RenameDocumentDialog({
  open,
  onClose,
  documentId,
  currentName,
}: RenameDocumentDialogProps) {
  const { t } = useTranslation(["documents"]);
  const [name, setName] = useState(currentName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const renameDocument = useRenameDocument();

  // Reset name when dialog opens with a new document
  useEffect(() => {
    if (open) {
      setName(currentName);
      // Focus input on next frame so the dialog has rendered
      requestAnimationFrame(() => inputRef.current?.select());
    }
  }, [open, currentName]);

  const handleSubmit = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === currentName) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    try {
      await renameDocument({
        id: documentId as Id<"documents">,
        fileName: trimmed,
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  }, [name, currentName, documentId, renameDocument, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  return (
    <ConfirmDialog open={open} onClose={onClose}>
      <ConfirmDialog.Title>
        {t("documents:renameDocumentDialog.title")}
      </ConfirmDialog.Title>
      <ConfirmDialog.Body>
        <div className="space-y-2">
          <label
            htmlFor="rename-input"
            className="text-[13px] text-secondary font-medium"
          >
            {t("documents:renameDocumentDialog.fileNameLabel")}
          </label>
          <input
            ref={inputRef}
            id="rename-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full h-9 px-3 rounded-lg bg-card border border-border text-[13px] text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors"
            placeholder={t(
              "documents:renameDocumentDialog.fileNamePlaceholder",
            )}
          />
        </div>
      </ConfirmDialog.Body>
      <ConfirmDialog.Actions
        onConfirm={handleSubmit}
        onCancel={onClose}
        confirmLabel={t("documents:renameDocumentDialog.save")}
        cancelLabel={t("documents:renameDocumentDialog.cancel")}
        loading={isSubmitting}
      />
    </ConfirmDialog>
  );
}
